import { useCallback, useEffect, useRef } from 'react';

type DraftEnvelope<T> = { savedAt: number; payload: T };

interface UseFormDraftPersistenceOptions<T> {
  key: string;
  enabled: boolean;
  value: T;
  onRestore: (payload: T) => void;
  debounceMs?: number;
  ttlMs?: number;
}

const DEFAULT_DEBOUNCE_MS = 600;
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;

export function useFormDraftPersistence<T>({
  key,
  enabled,
  value,
  onRestore,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  ttlMs = DEFAULT_TTL_MS,
}: UseFormDraftPersistenceOptions<T>) {
  const hasRestoredRef = useRef(false);

  const clearDraft = useCallback(() => {
    if (typeof window === 'undefined') return;
    sessionStorage.removeItem(key);
  }, [key]);

  useEffect(() => {
    if (typeof window === 'undefined' || !enabled || hasRestoredRef.current) return;
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      hasRestoredRef.current = true;
      return;
    }
    try {
      const parsed = JSON.parse(raw) as DraftEnvelope<T>;
      if (!parsed || typeof parsed.savedAt !== 'number') {
        sessionStorage.removeItem(key);
        return;
      }
      if (Date.now() - parsed.savedAt > ttlMs) {
        sessionStorage.removeItem(key);
      } else {
        onRestore(parsed.payload);
      }
    } catch {
      sessionStorage.removeItem(key);
    } finally {
      hasRestoredRef.current = true;
    }
  }, [enabled, key, onRestore, ttlMs]);

  useEffect(() => {
    if (typeof window === 'undefined' || !enabled || !hasRestoredRef.current) return;
    const timer = window.setTimeout(() => {
      const envelope: DraftEnvelope<T> = { savedAt: Date.now(), payload: value };
      sessionStorage.setItem(key, JSON.stringify(envelope));
    }, debounceMs);
    return () => window.clearTimeout(timer);
  }, [enabled, key, value, debounceMs]);

  return { clearDraft };
}
